import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { usePreviewFullPage, usePreviewFullPost, type FullPreviewData, type PostPreviewData } from '@wordpuppi/shared';

export const Route = createFileRoute('/sites/$siteSlug/preview')({
  component: PreviewPage,
});

function PreviewPage() {
  const { siteSlug } = Route.useParams();
  const search = Route.useSearch() as { key?: string; type?: 'post' | 'page' };
  const pageMutation = usePreviewFullPage(siteSlug);
  const postMutation = usePreviewFullPost(siteSlug);

  useEffect(() => {
    const loadPreview = async () => {
      const key = search.key;
      if (!key) {
        document.open();
        document.write('<h1>No preview key provided</h1>');
        document.close();
        return;
      }

      // AB#488 regression fix: read from localStorage (survives the opener's noopener
      // context break, unlike sessionStorage). Sweep stale keys, then delete-after-read.
      const TTL_MS = 10 * 60 * 1000;
      for (const k of Object.keys(localStorage)) {
        if (!k.startsWith('preview-')) continue;
        const ts = Number(k.slice('preview-'.length));
        if (k !== key && ts && Date.now() - ts > TTL_MS) localStorage.removeItem(k);
      }

      const dataJson = localStorage.getItem(key);
      localStorage.removeItem(key);
      if (!dataJson) {
        document.open();
        document.write('<h1>Preview data not found. It may have expired.</h1>');
        document.close();
        return;
      }

      try {
        let result;
        if (search.type === 'post') {
          const data: PostPreviewData = JSON.parse(dataJson);
          result = await postMutation.mutateAsync(data);
        } else {
          const data: FullPreviewData = JSON.parse(dataJson);
          result = await pageMutation.mutateAsync(data);
        }

        if (result.data && 'html' in result.data) {
          document.open();
          document.write(result.data.html);
          document.close();
        } else if (result.data && 'error' in result.data) {
          document.open();
          document.write(`<h1>Preview Error</h1><p>${result.data.error}</p>`);
          document.close();
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load preview';
        document.open();
        document.write(`<h1>Preview Error</h1><p>${msg}</p>`);
        document.close();
      }
    };

    loadPreview();
  }, [search.key]);

  return (
    <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
      Loading preview…
    </div>
  );
}
